import type {Pay4MeService, Pay4MeCampaignResponse} from '../../../infrastructure/services/Pay4MeService';
import type {IOrderRepository} from '../../../domain/repositories/IOrderRepository';

export interface CreatePay4MeCampaignParams {
    orderId: string;
    title: string;
    description: string;
    durationDays: number;
    userId: string;
}

export class CreatePay4MeCampaignUseCase {
    constructor(
        private readonly pay4meService: Pay4MeService,
        private readonly orderRepository: IOrderRepository
    ) {
    }

    async execute(params: CreatePay4MeCampaignParams): Promise<Pay4MeCampaignResponse> {
        // 1. Validate order exists and belongs to user
        const order = await this.orderRepository.findById(params.orderId);
        if (!order) {
            throw new Error('Order not found');
        }

        // 2. Validate order is eligible for Pay4Me
        this.validateOrderEligibility(order);

        // 3. Validate campaign parameters
        this.validateCampaignParams(params);

        // 4. Create Pay4Me campaign
        const campaign = await this.pay4meService.createCampaign(params.orderId, {
            title: params.title,
            description: params.description,
            durationDays: params.durationDays
        });

        return campaign;
    }

    private validateOrderEligibility(order: any): void {
        // Check if order has a payment plan (PSS)
        if (order.paymentType !== 'installment' && order.paymentType !== 'pay_later') {
            throw new Error('Only orders with payment plans are eligible for Pay4Me');
        }

        // Check if order has remaining balance
        if (order.getRemainingBalance() <= 0) {
            throw new Error('Order is already fully paid');
        }

        // Check if order is not cancelled or refunded
        if (order.status === 'cancelled' || order.status === 'refunded') {
            throw new Error('Cannot create campaign for cancelled or refunded orders');
        }

        // Check if order already has an active campaign
        if (order.campaignId) {
            throw new Error('Order already has an active Pay4Me campaign');
        }
    }

    private validateCampaignParams(params: CreatePay4MeCampaignParams): void {
        // Validate title
        if (!params.title || params.title.trim().length < 5) {
            throw new Error('Campaign title must be at least 5 characters long');
        }

        if (params.title.length > 100) {
            throw new Error('Campaign title cannot exceed 100 characters');
        }

        // Validate description
        if (!params.description || params.description.trim().length < 10) {
            throw new Error('Campaign description must be at least 10 characters long');
        }

        if (params.description.length > 500) {
            throw new Error('Campaign description cannot exceed 500 characters');
        }

        // Validate duration
        if (params.durationDays < 1 || params.durationDays > 90) {
            throw new Error('Campaign duration must be between 1 and 90 days');
        }
    }
}